import { SelectionChangeResponse, Tree, TreeGroup, TreeSearch, TreeSummary, TypeCounter } from "@rio-cloud/rio-uikit";
import { get, isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { IFilter } from "../useAssetTree";
import { useT } from "@hooks";

export interface IOptionsTreeConfig {
  groupId: string;
  ungroupedGroupId: string;
  ungroupedItemId: string;
  itemType: string;
  icon: string;
  groupLabel: string;
  ungroupedItemLabel: string;
  searchPlaceholder: string;
}

interface IOptionsTreeWithUngrouped {
  config: IOptionsTreeConfig;
  options?: IFilter[];
  selectedIds: string[];
  setSelectedIds: (items: string[]) => void;
  includeNull: boolean;
  setIncludeNull: (value: boolean) => void;
}

const OptionsTreeWithUngrouped = ({
  config,
  options,
  selectedIds,
  setSelectedIds,
  includeNull,
  setIncludeNull,
}: IOptionsTreeWithUngrouped) => {
  const t = useT();
  const [searchValue, setSearchValue] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([config.groupId, config.ungroupedGroupId]);

  const groups: TreeGroup[] = useMemo(
    () => [
      { id: config.groupId, name: t(config.groupLabel) },
      { id: config.ungroupedGroupId, name: t("assetTree.ungrouped"), position: "last" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, config]
  );

  const formatOptions = useMemo(
    () =>
      (options || []).map((option) => ({
        id: option.id,
        name: option.label,
        type: config.itemType,
        groupIds: [config.groupId],
      })),
    [options, config]
  );

  const ungroupedItem = useMemo(
    () => ({
      id: config.ungroupedItemId,
      name: t(config.ungroupedItemLabel),
      type: "filter",
      groupIds: [config.ungroupedGroupId],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, config]
  );

  const allItems = useMemo(() => [...formatOptions, ungroupedItem], [formatOptions, ungroupedItem]);

  const filteredItems = useMemo(
    () => allItems.filter((item) => get(item, "name", "").toLowerCase().includes(searchValue.toLowerCase())),
    [allItems, searchValue]
  );

  const filteredGroups = useMemo(() => {
    const available = new Set<string>();
    filteredItems.forEach((item) => item.groupIds?.forEach((id) => available.add(id)));
    return groups.filter((g) => available.has(g.id));
  }, [filteredItems, groups]);

  const selectedItems = useMemo(
    () => (includeNull ? [...selectedIds, config.ungroupedItemId] : selectedIds),
    [selectedIds, includeNull, config]
  );

  const selectedGroups = useMemo(() => {
    const result: string[] = [];
    if (formatOptions.length && formatOptions.every((option) => selectedIds.includes(option.id))) {
      result.push(config.groupId);
    }
    if (includeNull) {
      result.push(config.ungroupedGroupId);
    }
    return result;
  }, [formatOptions, selectedIds, includeNull, config]);

  const handleSelect = ({ items, groups: selectedGroupIds }: SelectionChangeResponse) => {
    const nextIds = new Set<string>(items);
    (selectedGroupIds || []).forEach((groupId) => {
      if (groupId === config.groupId) {
        formatOptions.forEach((option) => nextIds.add(option.id));
      }
      if (groupId === config.ungroupedGroupId) {
        nextIds.add(config.ungroupedItemId);
      }
    });
    setIncludeNull(nextIds.has(config.ungroupedItemId));
    nextIds.delete(config.ungroupedItemId);
    setSelectedIds(Array.from(nextIds));
  };

  const handleUpdateSearch = (value: string) => setSearchValue(value);

  return (
    <Tree
      groups={filteredGroups}
      items={filteredItems}
      expandedGroups={expandedGroups}
      onExpandGroupsChange={setExpandedGroups}
      selectedItems={selectedItems}
      selectedGroups={selectedGroups}
      onSelectionChange={handleSelect}
      hasMultiselect={!isEmpty(filteredItems)}
      summary={
        <TreeSummary>
          <TypeCounter icon={config.icon} value={formatOptions.length} />
        </TreeSummary>
      }
      search={
        <TreeSearch value={searchValue} placeholder={t(config.searchPlaceholder)} onChange={handleUpdateSearch} />
      }
    />
  );
};

export default OptionsTreeWithUngrouped;
